import api from '@/services/api';

const STUDIO_BASE = '/ai-studio';

export type InstanceSetupStatus = 'not_required' | 'not_started' | 'in_progress' | 'ready';

export interface InstanceSetupStatusDto {
  instanceId: string;
  agentId: string | null;
  agentCode: string | null;
  requiresSetup: boolean;
  status: InstanceSetupStatus;
  configured: boolean;
  messages: string[];
  summary: {
    siteName: string | null;
    siteLocation: string | null;
    resourceCount: number;
    demandCount: number;
    taskCount: number;
    horizonMinutes: number | null;
  };
  steps: {
    fields: boolean;
    review: boolean;
    site: boolean;
    data: boolean;
  };
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  error?: string;
}

export interface SetupStatusRow {
  instance_id: string;
  agent_id: string | null;
  agent_code: string | null;
  requires_setup: boolean;
  status: InstanceSetupStatus;
  configured: boolean;
  messages: string[];
  summary: {
    site_name: string | null;
    site_location: string | null;
    resource_count: number;
    demand_count: number;
    task_count: number;
    horizon_minutes: number | null;
  };
  steps: {
    fields?: boolean;
    review?: boolean;
    site?: boolean;
    data?: boolean;
  };
}

export function mapStatus(row: SetupStatusRow): InstanceSetupStatusDto {
  return {
    instanceId: row.instance_id,
    agentId: row.agent_id,
    agentCode: row.agent_code,
    requiresSetup: row.requires_setup,
    status: row.status,
    configured: row.configured,
    messages: row.messages,
    summary: {
      siteName: row.summary.site_name,
      siteLocation: row.summary.site_location,
      resourceCount: row.summary.resource_count,
      demandCount: row.summary.demand_count,
      taskCount: row.summary.task_count,
      horizonMinutes: row.summary.horizon_minutes,
    },
    steps: {
      fields: row.steps.fields ?? row.steps.site ?? false,
      review: row.steps.review ?? false,
      site: row.steps.site ?? false,
      data: row.steps.data ?? true,
    },
  };
}

export async function getInstanceSetupStatus(
  instanceId: string
): Promise<InstanceSetupStatusDto> {
  const response = await api.get<ApiEnvelope<SetupStatusRow>>(
    `${STUDIO_BASE}/instances/${instanceId}/setup-status`
  );
  return mapStatus(response.data.data);
}

export async function saveInstanceSetup(
  instanceId: string,
  payload: {
    site?: { name?: string; location?: string };
    schedulingWorkingSet?: Record<string, unknown>;
    fields?: Record<string, unknown>;
    setupNotes?: string;
    markComplete?: boolean;
  }
): Promise<InstanceSetupStatusDto> {
  const response = await api.put<ApiEnvelope<SetupStatusRow>>(
    `${STUDIO_BASE}/instances/${instanceId}/setup`,
    {
      site: payload.site,
      scheduling_working_set: payload.schedulingWorkingSet,
      fields: payload.fields,
      setup_notes: payload.setupNotes,
      mark_complete: payload.markComplete,
    }
  );
  return mapStatus(response.data.data);
}

export async function ingestInstanceSetupFile(
  instanceId: string,
  file: File,
  options?: { markComplete?: boolean; fieldKey?: string }
): Promise<InstanceSetupStatusDto> {
  const form = new FormData();
  form.append('file', file);
  if (options?.markComplete) {
    form.append('mark_complete', 'true');
  }
  if (options?.fieldKey) {
    form.append('field_key', options.fieldKey);
  }
  const response = await api.post<ApiEnvelope<SetupStatusRow>>(
    `${STUDIO_BASE}/instances/${instanceId}/ingest`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return mapStatus(response.data.data);
}

export interface InstanceSetupPatch {
  site?: { name?: string; location?: string };
  setup_notes?: string;
  fields?: Record<string, unknown>;
}

export interface SetupAssistMessage {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  suggestedSetupPatch?: InstanceSetupPatch;
}

interface SetupAssistMessageRow {
  id: string;
  role: string;
  content: string;
  created_at: string;
  suggestedSetupPatch?: InstanceSetupPatch;
}

function mapSetupAssistMessage(row: SetupAssistMessageRow): SetupAssistMessage {
  return {
    id: row.id,
    role: row.role,
    content: row.content,
    createdAt: row.created_at ?? (row as { createdAt?: string }).createdAt ?? '',
    suggestedSetupPatch: row.suggestedSetupPatch,
  };
}

export async function getInstanceSetupAssistSession(instanceId: string): Promise<{
  conversationId: string;
  instanceId: string;
  messages: SetupAssistMessage[];
  setupStatus: InstanceSetupStatusDto;
}> {
  const response = await api.get<
    ApiEnvelope<{
      conversationId: string;
      instanceId: string;
      messages: SetupAssistMessage[];
      setupStatus: SetupStatusRow;
    }>
  >(`${STUDIO_BASE}/instances/${instanceId}/setup/assist`);
  const data = response.data.data;
  return {
    conversationId: data.conversationId,
    instanceId: data.instanceId,
    messages: data.messages.map((row) => ({
      ...row,
      createdAt: row.createdAt,
    })),
    setupStatus: mapStatus(data.setupStatus),
  };
}

export async function sendInstanceSetupAssistMessage(
  instanceId: string,
  content: string
): Promise<{
  userMessage: SetupAssistMessage;
  assistantMessage: SetupAssistMessage;
  setupStatus: InstanceSetupStatusDto;
}> {
  const response = await api.post<
    ApiEnvelope<{
      userMessage: SetupAssistMessageRow;
      assistantMessage: SetupAssistMessageRow;
      setupStatus: SetupStatusRow;
    }>
  >(`${STUDIO_BASE}/instances/${instanceId}/setup/assist/messages`, { content });
  const data = response.data.data;
  return {
    userMessage: mapSetupAssistMessage(data.userMessage),
    assistantMessage: mapSetupAssistMessage(data.assistantMessage),
    setupStatus: mapStatus(data.setupStatus),
  };
}

export interface EffectiveAgentToolDto {
  toolName: string;
  toolKind: string;
  handlerRef: string;
  description: string;
}

export interface InstanceEffectiveAgent {
  agentId: string;
  agentCode: string | null;
  agentName: string;
  purpose: string | null;
  versionNumber: number;
  executionMode: string;
  industryPackName: string | null;
  packCodes: string[];
  tools: EffectiveAgentToolDto[];
}

interface EffectiveAgentRow {
  agent_id: string;
  agent_code: string | null;
  agent_name: string;
  purpose: string | null;
  version_number: number;
  execution_mode: string;
  industry_pack_name: string | null;
  pack_codes: string[];
  tools: Array<{
    tool_name: string;
    tool_kind: string;
    handler_ref: string;
    description: string;
  }>;
}

export async function getInstanceEffectiveAgent(
  instanceId: string
): Promise<InstanceEffectiveAgent> {
  const response = await api.get<ApiEnvelope<EffectiveAgentRow>>(
    `${STUDIO_BASE}/instances/${instanceId}/effective-agent`
  );
  const row = response.data.data;
  return {
    agentId: row.agent_id,
    agentCode: row.agent_code,
    agentName: row.agent_name,
    purpose: row.purpose,
    versionNumber: row.version_number,
    executionMode: row.execution_mode,
    industryPackName: row.industry_pack_name,
    packCodes: row.pack_codes ?? [],
    tools: (row.tools ?? []).map((tool) => ({
      toolName: tool.tool_name,
      toolKind: tool.tool_kind,
      handlerRef: tool.handler_ref,
      description: tool.description,
    })),
  };
}
